import React, { useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { productPlaceholder } from "../../data/products";
import ProductImage from "./ProductImage";

export default function ProductGallery({ images = [], productName }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const touchStart = useRef(null);
  const galleryImages = images.length ? images : [productPlaceholder];
  const activeIndex = Math.min(selectedIndex, galleryImages.length - 1);
  const hasMultiple = galleryImages.length > 1;
  const selectImage = (index) => setSelectedIndex(Math.max(0, Math.min(index, galleryImages.length - 1)));

  const startSwipe = (event) => {
    touchStart.current = event.touches.length === 1
      ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
      : null;
  };
  const finishSwipe = (event) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || event.changedTouches.length !== 1 || event.touches.length) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy)) {
      selectImage(activeIndex + (dx < 0 ? 1 : -1));
    }
  };

  return (
    <div className={`product-gallery${hasMultiple ? " has-multiple" : ""}`} role="region"
      aria-roledescription="carousel" aria-label={`${productName} images`}>
      {hasMultiple && (
        <div className="product-gallery-thumbnail-rail">
          <div className="product-gallery-thumbnails" role="group" aria-label="Choose a product image">
            {galleryImages.map((image, index) => (
              <button key={`${image.src}-${index}`} type="button" className="product-gallery-thumbnail"
                aria-label={`Show image ${index + 1} of ${galleryImages.length}`} aria-pressed={activeIndex === index}
                onClick={() => selectImage(index)}>
                <ProductImage image={image} eager={index === 0} decorative />
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="product-gallery-viewport" onTouchStart={startSwipe} onTouchEnd={finishSwipe}
        onTouchCancel={() => { touchStart.current = null; }}>
        <ProductImage key={galleryImages[activeIndex].src} image={galleryImages[activeIndex]} eager />
        {hasMultiple && (
          <div className="product-gallery-arrows">
            <button type="button" className="product-gallery-previous" aria-label="Previous product image"
              disabled={activeIndex === 0} onClick={() => selectImage(activeIndex - 1)}>
              <FiChevronLeft aria-hidden="true" />
            </button>
            <button type="button" className="product-gallery-next" aria-label="Next product image"
              disabled={activeIndex === galleryImages.length - 1} onClick={() => selectImage(activeIndex + 1)}>
              <FiChevronRight aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      {hasMultiple && (
        <>
          <div className="product-gallery-dots" role="group" aria-label="Choose a product image">
            {galleryImages.map((image, index) => (
              <button key={`${image.src}-${index}`} type="button" aria-label={`Show image ${index + 1} of ${galleryImages.length}`}
                aria-current={activeIndex === index ? "true" : undefined} onClick={() => selectImage(index)}>
                <span aria-hidden="true" />
              </button>
            ))}
          </div>
          <span className="product-gallery-status visually-hidden" aria-live="polite" aria-atomic="true">
            Image {activeIndex + 1} of {galleryImages.length}
          </span>
        </>
      )}
    </div>
  );
}
